import React, { useState } from 'react';
import {
  Box, Button, Chip, IconButton, Snackbar, Stack, Tooltip, Typography,
} from '@mui/material';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { openInvoicePdf } from '../api/client';

// ----------------------------------------------------------------------------
// Shared "where does this price come from?" rendering.
// Every €/Einheit in Skonto is derived as:
//   printed  quantity × unit @ unit_price = total
//   pack     base_quantity normalized_unit   (read via pack_method, pack_confidence)
//   result   total ÷ base_quantity = unit_price_per_einheit
// This file renders that chain the same way on every screen.
// ----------------------------------------------------------------------------

/** Loose input shape — every field optional so old rows without provenance never crash. */
export interface ProvenanceInput {
  raw?: string | null;
  quantity?: number | null;
  unit?: string | null;
  unit_price?: number | null;
  total?: number | null;
  base_quantity?: number | null;
  normalized_unit?: string | null;
  pack_method?: string | null;
  pack_confidence?: number | null;
  unit_price_per_einheit?: number | null;
  invoice_id?: number | null;
  invoice_no?: string | null;
  invoice_date?: string | null;
}

// ----------------------------- Format helpers -------------------------------

export const fmtEUR = (n: number | null | undefined, maxFraction = 2): string =>
  n == null || isNaN(n)
    ? '—'
    : n.toLocaleString('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: maxFraction });

/** €/Einheit: up to 4 fraction digits so cents-per-kg differences stay visible. */
export const fmtEURUnit = (n: number | null | undefined): string => fmtEUR(n, 4);

export const fmtQty = (n: number | null | undefined): string =>
  n == null || isNaN(n) ? '—' : n.toLocaleString('de-DE', { maximumFractionDigits: 3 });

export const fmtDate = (s: string | null | undefined): string => {
  if (!s) return '—';
  const d = new Date(s);
  return isNaN(d.getTime()) ? s : d.toLocaleDateString('de-DE');
};

/** pack_confidence comes as 0..1 (or, defensively, already as a percentage). */
export const fmtConfidence = (c: number | null | undefined): string => {
  if (c == null || isNaN(c)) return '—';
  const pct = c <= 1 ? c * 100 : c;
  return `${Math.round(pct)} %`;
};

/** `2 × KT @ 43,59 € = 87,18 €` — the printed invoice line in numbers. */
export const fmtPrintedLine = (p: ProvenanceInput): string => {
  const qty = p.quantity != null ? fmtQty(p.quantity) : '?';
  const unit = p.unit || '';
  const price = p.unit_price != null ? fmtEUR(p.unit_price) : '?';
  const total = p.total != null ? fmtEUR(p.total) : '?';
  return `${qty} × ${unit}`.trim() + ` @ ${price} = ${total}`;
};

/** `87,18 € ÷ 9 l = 9,69 €/l` — or null when the chain is incomplete. */
export const fmtFormula = (p: ProvenanceInput): string | null => {
  if (p.total == null || p.base_quantity == null || !p.base_quantity || p.unit_price_per_einheit == null) return null;
  const unit = p.normalized_unit || 'Einheit';
  return `${fmtEUR(p.total)} ÷ ${fmtQty(p.base_quantity)} ${unit} = ${fmtEURUnit(p.unit_price_per_einheit)}/${unit}`;
};

/** `Rechnung 41261805 · 06.08.2026` */
export const fmtInvoiceRef = (p: ProvenanceInput): string => {
  const parts: string[] = [];
  if (p.invoice_no) parts.push(`Rechnung ${p.invoice_no}`);
  else if (p.invoice_id != null) parts.push(`Rechnung #${p.invoice_id}`);
  if (p.invoice_date) parts.push(fmtDate(p.invoice_date));
  return parts.length ? parts.join(' · ') : 'Rechnung unbekannt';
};

/** Chip shown wherever a line has no €/Einheit yet. */
export function NichtNormalisiertChip({ size = 'small' }: { size?: 'small' | 'medium' }) {
  return (
    <Tooltip title="Gebinde/Inhalt konnte nicht sicher gelesen werden — diese Position fließt in keinen Vergleich ein, bis sie korrigiert ist.">
      <Chip size={size} label="nicht normalisiert" variant="outlined"
        sx={{ color: 'text.secondary', borderColor: 'divider', bgcolor: 'action.hover' }} />
    </Tooltip>
  );
}

// ----------------------------- PDF button -----------------------------------

interface InvoicePdfButtonProps {
  invoiceId?: number | null;
  /** Optional label context — shown in the tooltip only. */
  invoiceNo?: string | null;
  /** 'icon' = compact IconButton for tables; 'button' = outlined "PDF öffnen". */
  variant?: 'icon' | 'button';
  /** If given, notices (e.g. "kein PDF hinterlegt") go here instead of an own Snackbar. */
  onNotice?: (msg: string) => void;
}

/**
 * Opens the original invoice PDF in a new tab. Handles 404 ("kein PDF
 * hinterlegt") and network errors with a friendly notice instead of crashing.
 */
export function InvoicePdfButton({ invoiceId, invoiceNo, variant = 'icon', onNotice }: InvoicePdfButtonProps) {
  const [busy, setBusy] = useState(false);
  const [ownNotice, setOwnNotice] = useState<string | null>(null);

  const notify = (msg: string) => (onNotice ? onNotice(msg) : setOwnNotice(msg));

  const open = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (invoiceId == null) { notify('Zu dieser Position ist keine Rechnung verknüpft.'); return; }
    setBusy(true);
    try {
      await openInvoicePdf(invoiceId);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 404) notify(`Für Rechnung ${invoiceNo || `#${invoiceId}`} ist kein PDF hinterlegt.`);
      else if (status === 401 || status === 403) notify('Keine Berechtigung, das PDF zu öffnen.');
      else notify('PDF konnte nicht geladen werden. Bitte später erneut versuchen.');
    } finally {
      setBusy(false);
    }
  };

  const disabled = busy || invoiceId == null;
  const title = invoiceId == null
    ? 'Keine Rechnung verknüpft'
    : `Original-PDF öffnen${invoiceNo ? ` (Rechnung ${invoiceNo})` : ''}`;

  const control = variant === 'button' ? (
    <Button size="small" variant="outlined" startIcon={<PictureAsPdfOutlinedIcon />}
      onClick={open} disabled={disabled} sx={{ whiteSpace: 'nowrap' }}>
      PDF öffnen
    </Button>
  ) : (
    <IconButton size="small" onClick={open} disabled={disabled} aria-label="PDF öffnen" color="primary">
      <PictureAsPdfOutlinedIcon fontSize="small" />
    </IconButton>
  );

  return (
    <>
      {/* Tooltip needs an enabled child to attach listeners; wrap in span. */}
      <Tooltip title={title}><span>{control}</span></Tooltip>
      {!onNotice && (
        <Snackbar open={!!ownNotice} autoHideDuration={4000} onClose={() => setOwnNotice(null)}
          message={ownNotice} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
      )}
    </>
  );
}

// ----------------------------- Provenance block -----------------------------

interface ProvenanceBlockProps {
  line: ProvenanceInput;
  /** Show the invoice reference + PDF button (default true). */
  showInvoice?: boolean;
  onNotice?: (msg: string) => void;
  /** Render on a dark tooltip background (lighter text colours). */
  onDark?: boolean;
}

/**
 * The full derivation chain for one invoice line:
 *   raw text → printed numbers → pack read → €/Einheit formula → invoice ref.
 * Every line is guarded; missing provenance renders a short "unbekannt".
 */
export function ProvenanceBlock({ line, showInvoice = true, onNotice, onDark = false }: ProvenanceBlockProps) {
  const muted = onDark ? 'rgba(255,255,255,0.75)' : 'text.secondary';
  const strong = onDark ? '#fff' : 'text.primary';
  const formula = fmtFormula(line);
  const hasPack = line.base_quantity != null && !!line.base_quantity;

  return (
    <Stack spacing={0.4} sx={{ minWidth: 0 }}>
      {line.raw ? (
        <Typography component="div" variant="caption"
          sx={{
            fontFamily: '"JetBrains Mono", "Fira Mono", Consolas, monospace', fontSize: 11.5,
            color: muted, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            bgcolor: onDark ? 'rgba(255,255,255,0.08)' : 'action.hover',
            px: 0.75, py: 0.4, borderRadius: 0.5,
          }}>
          {line.raw}
        </Typography>
      ) : (
        <Typography variant="caption" sx={{ color: muted, fontStyle: 'italic' }}>
          Rohzeile nicht gespeichert
        </Typography>
      )}

      <Typography variant="caption" sx={{ color: strong }}>
        <Box component="span" sx={{ color: muted }}>Gedruckt:&nbsp;</Box>
        {fmtPrintedLine(line)}
      </Typography>

      <Typography variant="caption" sx={{ color: strong }}>
        <Box component="span" sx={{ color: muted }}>Gebinde:&nbsp;</Box>
        {hasPack ? (
          <>
            → {fmtQty(line.base_quantity)} {line.normalized_unit || ''}
            <Box component="span" sx={{ color: muted }}>
              &nbsp;via {line.pack_method || 'unbekannt'} · {fmtConfidence(line.pack_confidence)}
            </Box>
          </>
        ) : (
          <Box component="span" sx={{ color: muted }}>
            nicht gelesen
            {line.pack_method ? ` (Methode ${line.pack_method}` : ''}
            {line.pack_method && line.pack_confidence != null ? `, Sicherheit ${fmtConfidence(line.pack_confidence)}` : ''}
            {line.pack_method ? ')' : ''}
          </Box>
        )}
      </Typography>

      <Typography variant="caption" sx={{ color: strong }}>
        <Box component="span" sx={{ color: muted }}>Ergebnis:&nbsp;</Box>
        {formula ? formula : (
          line.unit_price_per_einheit != null
            ? `${fmtEURUnit(line.unit_price_per_einheit)}/${line.normalized_unit || 'Einheit'}`
            : <Box component="span" sx={{ color: muted }}>kein €/Einheit (nicht normalisiert)</Box>
        )}
      </Typography>

      {showInvoice && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ pt: 0.25 }}>
          <Typography variant="caption" sx={{ color: muted }}>{fmtInvoiceRef(line)}</Typography>
          <InvoicePdfButton invoiceId={line.invoice_id} invoiceNo={line.invoice_no} variant="button" onNotice={onNotice} />
        </Stack>
      )}
    </Stack>
  );
}

/** Small info icon that reveals the provenance chain on hover/tap. */
export function ProvenanceTooltip({ line }: { line: ProvenanceInput }) {
  return (
    <Tooltip
      arrow
      placement="left"
      enterTouchDelay={0}
      title={<Box sx={{ p: 0.5, maxWidth: 420 }}><ProvenanceBlock line={line} showInvoice={false} onDark /></Box>}
    >
      <IconButton size="small" aria-label="Herkunft anzeigen" onClick={(e) => e.stopPropagation()}>
        <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
      </IconButton>
    </Tooltip>
  );
}
